'use client';

import { useCallback } from 'react';
import { decodeName, User, UserAccount } from '@velocity-exchange/sdk';
import { getUserKey } from '@velocity-exchange/common';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useVelocityAccountsStore, {
	ACCOUNT_INITIAL_STATE,
	AccountData,
} from 'src/stores/useVelocityAccountsStore';
import { DEFAULT_PUBLIC_KEY } from 'src/@types/types';

export const useGenerateInitialStateForUserAccount = () => {
	const getAccountsState = useVelocityAccountsStore((s) => s.get);
	const getVelocityStore = useVelocityStore((s) => s.get);
	const generateInitialStateForUserAccount = useCallback(
		(userClient: User, userAccount: UserAccount): AccountData => {
			const walletAdapter = getVelocityStore().wallet.current.adapter;
			if (!walletAdapter?.publicKey) return ACCOUNT_INITIAL_STATE();
			const accountsState = getAccountsState();
			const userAccountToSubscribe =
				userClient?.getUserAccount() ?? userAccount;
			const userId = userAccountToSubscribe.subAccountId;
			const userKey = getUserKey(userId, userAccountToSubscribe.authority);

			const accountData: AccountData = accountsState.accounts[
				userKey
			] as AccountData;
			let accountState;

			// make sure we don't overwrite account data if it's already loaded
			if (accountData) {
				accountState = { ...accountData, client: userClient };
			} else {
				// initialize account state in store
				accountState = ACCOUNT_INITIAL_STATE(userKey);

				accountState = {
					...accountState,
					userKey: userKey,
					userId: userId,
					name: decodeName(userAccountToSubscribe.name),
					pubKey: userClient.getUserAccountPublicKey(),
					authority: userAccountToSubscribe.authority,
					client: userClient,
					marginEnabled:
						userClient.getUserAccountOrThrow().isMarginTradingEnabled,
					delegate: userAccount.delegate,
					isDelegatedTo: userAccount.delegate.equals(walletAdapter.publicKey),
					isDelegatedAway:
						!userAccount.delegate.equals(walletAdapter.publicKey) &&
						!userAccount.delegate.equals(DEFAULT_PUBLIC_KEY),
					poolId: userAccount.poolId,
				};
			}

			return accountState;
		},
		[getAccountsState]
	);

	return generateInitialStateForUserAccount;
};
